import {
  collection,
  doc,
  type CollectionReference,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore';
import { z } from 'zod';

/**
 * Chaves de conteúdo editável do app (termos de uso, política de privacidade).
 * Cada chave corresponde a um documento em AppContent/{key}.
 */
export const APP_CONTENT_KEYS = ['termsOfUse', 'privacyPolicy'] as const;
export type AppContentKey = (typeof APP_CONTENT_KEYS)[number];

export const appContentCurrentSchema = z.object({
  currentVersion: z.number().int().nonnegative(),
  currentContent: z.string(),
  lastChangeNotes: z.string().nullable().optional(),
  requiresReacceptance: z.boolean().nullable().optional(),
  createdAt: z.date().nullable().optional(),
  createdByUid: z.string().nullable().optional(),
  createdByName: z.string().nullable().optional(),
  updatedAt: z.date().nullable().optional(),
  updatedByUid: z.string().nullable().optional(),
  updatedByName: z.string().nullable().optional(),
  publishedAt: z.date().nullable().optional(),
});

export type AppContentCurrentEntity = z.infer<typeof appContentCurrentSchema>;

export const appContentVersionSchema = z.object({
  version: z.number().int().positive(),
  content: z.string(),
  changeNotes: z.string(),
  requiresReacceptance: z.boolean(),
  createdAt: z.date().nullable().optional(),
  createdByUid: z.string().nullable().optional(),
  createdByName: z.string().nullable().optional(),
  publishedAt: z.date().nullable().optional(),
});

export type AppContentVersionEntity = z.infer<typeof appContentVersionSchema>;

const COLLECTION_NAME = 'AppContent';
const VERSIONS_SUBCOLLECTION = 'versions';

export const AppContentModel = {
  currentSchema: appContentCurrentSchema,
  versionSchema: appContentVersionSchema,
  collectionName: COLLECTION_NAME,
  versionsSubcollection: VERSIONS_SUBCOLLECTION,

  documentRef(firestore: Firestore, key: AppContentKey): DocumentReference {
    return doc(firestore, COLLECTION_NAME, key);
  },

  versionsCollectionRef(
    firestore: Firestore,
    key: AppContentKey
  ): CollectionReference {
    return collection(
      firestore,
      COLLECTION_NAME,
      key,
      VERSIONS_SUBCOLLECTION
    );
  },

  versionDocumentRef(
    firestore: Firestore,
    key: AppContentKey,
    versionId: string
  ): DocumentReference {
    return doc(
      firestore,
      COLLECTION_NAME,
      key,
      VERSIONS_SUBCOLLECTION,
      versionId
    );
  },

  fromCurrentJson(data: unknown): AppContentCurrentEntity {
    return appContentCurrentSchema.parse(data);
  },

  fromVersionJson(data: unknown): AppContentVersionEntity {
    return appContentVersionSchema.parse(data);
  },

  toCurrentJson(entity: AppContentCurrentEntity): Record<string, unknown> {
    const parsed = appContentCurrentSchema.parse(entity) as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(parsed)) {
      const v = parsed[k];
      if (v instanceof Date) {
        out[k] = v;
      } else if (v === undefined) {
        out[k] = null;
      } else {
        out[k] = v;
      }
    }
    return out;
  },

  toVersionJson(entity: AppContentVersionEntity): Record<string, unknown> {
    const parsed = appContentVersionSchema.parse(entity) as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(parsed)) {
      const v = parsed[k];
      if (v instanceof Date) {
        out[k] = v;
      } else if (v === undefined) {
        out[k] = null;
      } else {
        out[k] = v;
      }
    }
    return out;
  },
} as const;
